/**
 * 地图编辑器动态地图新增/编辑表单
 *
 * 职责：
 * 渲染表单 UI，组合各 Hook 完成业务逻辑
 *
 * 模块拆分：
 * - hooks/useFormInit.ts      — 初始化/回显（编辑/新增/限速三条路径）
 * - hooks/useFormLinkage.ts   — 字段联动规则
 * - hooks/useFormSubmit.ts    — 提交组装 + 校验
 * - hooks/useTimeLimit.ts     — 分时段管理
 * - hooks/useFormOptions.ts   — 选项计算
 */
import React from 'react';
import { Alert, Button, Checkbox, Form, Radio, Select, Input, Spin } from 'antd';
import { AnchorLayerType, EffectDateType, EffectVehicleType, EffectTimeType, LightMapAssistType, LightMapAssistTypeText } from '@/utils/constant';
import {
  typeOptions,
  weekLimitOptions,
  effectTimeTypeOptions,
  subTypeOptions,
  effectiveMapOptions,
} from '../utils/config';
import TimeLimit from './TimeLimit';
import { useFormInit, DEFAULT_FORM_VALUE } from '../hooks/useFormInit';
import { useFormLinkage } from '../hooks/useFormLinkage';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { useTimeLimit } from '../hooks/useTimeLimit';
import { useFormOptions } from '../hooks/useFormOptions';
import { useRoadTestItems } from '../hooks/useRoadTestItems';
import { useVehicleOptions } from '../hooks/useVehicleOptions';
import type { NavigationEnumOptions } from '../utils/navigation';

/** 标准类型选项：编辑轻图时排除 LD_ASSIST */
const standardTypeOptions = typeOptions.filter(
  (option) => option.value !== AnchorLayerType.LD_ASSIST
);

type FormProps = {
  type: 'add' | 'edit';
  defaultData?: any;
  saveFormInfo: AnyFunc;
  quitEditForm: AnyFunc;
  getLaneCoordinates: AnyFunc;
  onLightMapSubTypeChange?: (subType: LightMapAssistType) => void;
  onLightMapTypeSelect?: () => void;
  navigationEnums: NavigationEnumOptions;
  navigationEnumStatus: 'loading' | 'ready' | 'error';
  retryNavigationEnums: () => void;
};

/**
 * 表单浮层挂载到抽屉根容器。
 * 既避免被高层级右侧抽屉遮住，也避免挂到表单项父级导致 ResizeObserver loop。
 */
const getDrawerPopupContainer = (triggerNode: HTMLElement) =>
  (triggerNode.closest('.map-editor-drawer-container') as HTMLElement | null) ??
  triggerNode.parentElement ??
  triggerNode;

const EditForm = ({
  type,
  defaultData,
  quitEditForm,
  saveFormInfo,
  getLaneCoordinates,
  onLightMapSubTypeChange,
  onLightMapTypeSelect,
  navigationEnums,
  navigationEnumStatus,
  retryNavigationEnums,
}: FormProps) => {
  const [form] = Form.useForm();

  // 初始化
  const { fetchLaneInfo, isLaneLoading } = useFormInit({ form, defaultData, getLaneCoordinates });

  // 路测关注区
  const { roadTestItems, isLoadingItems, itemsError, fetchRoadTestItemList } = useRoadTestItems();

  // 联动
  const { onValuesChange } = useFormLinkage({
    form,
    onRoadTestType: fetchRoadTestItemList,
    onLightMapTypeSelect,
  });

  // 提交
  const { isSubmitting, handleSubmit } = useFormSubmit({ form, saveFormInfo, roadTestItems });

  // 分时段
  const { saveTimeLimit } = useTimeLimit({ form });

  // 获取当前 type 值（用于选项计算）
  const currentType = Form.useWatch('type', form) || DEFAULT_FORM_VALUE.type;

  // 选项计算
  const effectiveMapList = Form.useWatch('effectiveMapList', form);
  const { vehicleEffectOptions, effectDateTypeRadioOptions } = useFormOptions(currentType, effectiveMapList);

  // 监听联动字段
  const effectVehicleType = Form.useWatch('effectVehicleType', form) || DEFAULT_FORM_VALUE.effectVehicleType;
  const vehicleOptions = useVehicleOptions(effectVehicleType !== EffectVehicleType.ALL_EFFECT);
  const effectDateType = Form.useWatch('effectDateType', form) || DEFAULT_FORM_VALUE.effectDateType;
  const effectTimeType = Form.useWatch('effectTimeType', form);
  const timeLimit = Form.useWatch('timeLimit', form) || DEFAULT_FORM_VALUE.timeLimit;
  // preserve: true 让 useWatch 读取完整 store，避免 laneIdList 未注册字段被当成 undefined
  const laneIdList = Form.useWatch('laneIdList', { form, preserve: true });

  // 轻图编辑模式：隐藏类型选择和其他字段，只显示备注
  const isLightMapEdit = type === 'edit' && defaultData?.type === AnchorLayerType.LD_ASSIST;
  const isNavigationType = currentType === AnchorLayerType.NAVIGATION;
  const navigationEnumsReady = navigationEnumStatus === 'ready';

  // 限速类型、非加载中、且无车道中心线时，显示蒙层禁用交互
  // 加载期间不展示蒙层，避免接口成功时视觉上闪一下
  const showLaneOverlay =
    currentType === AnchorLayerType.SPEED_LIMIT &&
    !isLaneLoading &&
    (!laneIdList || laneIdList.length === 0);

  return (
    <>
      <div className="quit-form" onClick={quitEditForm}>
        X
      </div>
      <div className="edit-form-scroll">
        <Form
          form={form}
          onValuesChange={onValuesChange}
          layout="vertical"
          className="edit-form"
        >
          {/* 类型选择：轻图编辑时隐藏 */}
          {!isLightMapEdit && (
            <>
              <Form.Item
                label="请选择类型"
                name="type"
                key="type"
                rules={[{ required: true, message: '请选择类型' }]}
                hidden={type !== 'add'}
              >
                <Radio.Group
                  options={type === 'add' ? typeOptions : standardTypeOptions}
                  onChange={(v) => fetchLaneInfo(v.target.value)}
                />
              </Form.Item>

              {/* 限速类型：车道中心线 */}
              {currentType === AnchorLayerType.SPEED_LIMIT && laneIdList?.length > 0 && (
                <Form.Item label="已选择车道中心线" name="laneIdList" key="laneIdList">
                  <div className="laneIdList">
                    {laneIdList.map((id: string) => (
                      <div className="laneId" key={id}>{id}</div>
                    ))}
                  </div>
                </Form.Item>
              )}

              {/* 限速类型：限速值 - 仅当有车道中心线时才显示 */}
              {currentType === AnchorLayerType.SPEED_LIMIT && laneIdList?.length > 0 && (
                <Form.Item
                  label="请输入速度限制(单位km/h)"
                  name="speedLimit"
                  key="speedLimit"
                  rules={[
                    { required: true, message: '请输入速度限制' },
                    { pattern: /^[1-9]\d*$/g, message: '请输入正整数' },
                  ]}
                >
                  <Input placeholder="请输入" />
                </Form.Item>
              )}

              {/* 属性标注区：属性类型 */}
              {currentType === AnchorLayerType.ATTRIBUTE_ANNOTATION && (
                <Form.Item
                  label="请选择属性类型"
                  name="subType"
                  key="subType"
                  rules={[{ required: true, message: '请选择属性类型' }]}
                  hidden={type !== 'add'}
                >
                  <Radio.Group className="attribute-radio-group">
                    {subTypeOptions.map((v: any) => (
                      <Radio value={v.value} className="attribute-radio" key={v.value}>
                        <img src={v.imgUrl} />
                        <span>{v.label}</span>
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              )}

              {/* 路测关注区：测试项（仅创建时展示） */}
              {currentType === AnchorLayerType.ROAD_TEST_FOCUS_AREA && type !== 'edit' && (
                <Form.Item
                  label="请选择测试项"
                  name="subType"
                  key="roadTestSubType"
                  rules={[{ required: true, message: '请选择测试项' }]}
                >
                  {isLoadingItems ? (
                    <Spin />
                  ) : itemsError ? (
                    <div style={{ color: '#fff' }}>{itemsError}</div>
                  ) : (
                    <Radio.Group className="attribute-radio-group">
                      {roadTestItems.map((item) => (
                        <Radio value={item.itemNo} className="attribute-radio" key={item.itemNo}>
                          <span>{item.itemName}</span>
                        </Radio>
                      ))}
                    </Radio.Group>
                  )}
                </Form.Item>
              )}

              {/* 轻图测试：子类型选择 */}
              {currentType === AnchorLayerType.LD_ASSIST && (
                <Form.Item
                  label="请选择子类型"
                  name="subType"
                  key="lightMapSubType"
                  rules={[{ required: true, message: '请选择子类型' }]}
                  hidden={type !== 'add'}
                >
                  <Radio.Group
                    className="light-map-subtype-group"
                    onChange={(e) => onLightMapSubTypeChange?.(e.target.value)}
                  >
                    <Radio value={LightMapAssistType.BUFFER_ZONE}>
                      {LightMapAssistTypeText.get(LightMapAssistType.BUFFER_ZONE)}
                    </Radio>
                    <Radio value={LightMapAssistType.WARNING_ZONE}>
                      {LightMapAssistTypeText.get(LightMapAssistType.WARNING_ZONE)}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              )}

              {/* 道路导航区：拓扑关系 + 行驶方向 */}
              {currentType === AnchorLayerType.NAVIGATION && (
                <>
                  {navigationEnumStatus !== 'ready' && (
                    <Alert
                      type={navigationEnumStatus === 'error' ? 'error' : 'info'}
                      showIcon
                      message={
                        navigationEnumStatus === 'error'
                          ? '导航枚举加载失败，暂不能提交'
                          : '正在加载导航枚举'
                      }
                      action={
                        navigationEnumStatus === 'error' ? (
                          <Button type="link" size="small" onClick={retryNavigationEnums}>
                      重试
                          </Button>
                        ) : undefined
                      }
                    />
                  )}
                  <Form.Item
                    label="请选择拓扑关系"
                    name="topology"
                    rules={[{ required: true, message: '请选择拓扑关系' }]}
                  >
                    <Radio.Group
                      options={navigationEnums.topology}
                      disabled={navigationEnumStatus !== 'ready'}
                    />
                  </Form.Item>
                  <Form.Item
                    label="请选择行驶方向"
                    name="direction"
                    rules={[
                      { required: true, message: '请选择行驶方向' },
                      { type: 'array', min: 1, message: '请选择至少一个行驶方向' },
                    ]}
                  >
                    <Checkbox.Group
                      options={navigationEnums.direction}
                      disabled={navigationEnumStatus !== 'ready'}
                    />
                  </Form.Item>
                </>
              )}
              {/* 生效车范围（封路障碍物专用） */}
              {currentType === AnchorLayerType.ROAD_CLOSED && (
                <Form.Item
                  label="生效车范围"
                  name="effectiveMapList"
                  key="effectiveMapList"
                  rules={[{ required: true, message: '请选择生效车范围' }]}
                >
                  <Checkbox.Group options={effectiveMapOptions} />
                </Form.Item>
              )}
              {/* 车辆生效范围 */}
              <Form.Item
                label="请选择车辆生效范围"
                name="effectVehicleType"
                key="effectVehicleType"
                rules={[{ required: true, message: '请选择车辆生效范围' }]}
              >
                <Radio.Group options={vehicleEffectOptions} />
              </Form.Item>

              {/* 车辆选择（部分生效时） */}
              {effectVehicleType !== EffectVehicleType.ALL_EFFECT && (
                <Form.Item
                  name="vehicleLimit"
                  label=""
                  rules={[{ required: true, message: '请选择车辆' }]}
                >
                  <Select
                    options={vehicleOptions}
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="请选择车辆"
                    maxTagTextLength={50}
                    getPopupContainer={getDrawerPopupContainer}
                  />
                </Form.Item>
              )}

              {/* 生效类别 */}
              <Form.Item
                label="请选择生效类别"
                name="effectDateType"
                key="effectDateType"
                rules={[{ required: true, message: '请选择生效类别' }]}
              >
                <Radio.Group options={effectDateTypeRadioOptions} />
              </Form.Item>

              {/* 周期生效：生效日期 */}
              {effectDateType === EffectDateType.SEASONAL && (
                <Form.Item
                  name="weekLimit"
                  label="生效日期"
                  rules={[{ required: true, message: '请选择生效日期' }]}
                >
                  <Select
                    options={weekLimitOptions}
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="请选择生效日期"
                    getPopupContainer={getDrawerPopupContainer}
                  />
                </Form.Item>
              )}

              {/* 周期生效：生效时间 */}
              {effectDateType === EffectDateType.SEASONAL && (
                <Form.Item
                  name="effectTimeType"
                  label="生效时间"
                  rules={[{ required: true, message: '请选择生效时间' }]}
                >
                  <Radio.Group options={effectTimeTypeOptions} />
                </Form.Item>
              )}

              {/* 分时段 */}
              {effectTimeType === EffectTimeType.PERIOD_TIME && (
                <Form.Item name="timeLimit">
                  <TimeLimit initialValue={timeLimit} saveTimeLimit={saveTimeLimit} />
                </Form.Item>
              )}
            </>
          )}

          {/* 备注 */}
          <Form.Item
            label={currentType === AnchorLayerType.LD_ASSIST && type === 'edit' ? '备注' : '备注（必填）'}
            name="remark"
            rules={[{ required: true, message: '备注信息不能为空' }]}
          >
            <Input.TextArea rows={2} maxLength={80} showCount />
          </Form.Item>
        </Form>

        {/* 限速类型且无车道中心线时的蒙层 */}
        {showLaneOverlay && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 4,
            }}
          >
            <div style={{ textAlign: 'center', color: '#fff', padding: '0 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>区域内未覆盖道路中心线</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>请重新绘制包含道路中心线的区域</div>
            </div>
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="form-btn-container">
        <Button className="repaint-btn" onClick={quitEditForm}>
          {type === 'add' ? '重新绘制' : '取消'}
        </Button>
        <Button
          type="primary"
          className="create-btn"
          onClick={handleSubmit}
          disabled={isSubmitting || showLaneOverlay || (currentType === AnchorLayerType.NAVIGATION && navigationEnumStatus !== 'ready')}
        >
          {type === 'add' ? '创建' : '确定编辑'}
        </Button>
      </div>
    </>
  );
};

export default React.memo(EditForm);
